import { Button, Input, MagellanPopup } from 'components/atoms';
import LanguageSelectCard from 'components/molecules/card/language-select/LanguageSelectCard';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface ILanguageOption {
  label: string;
  value: string;
}

interface IApplicationLanguagePopupFormProps {
  trigger?: JSX.Element | ((isOpen: boolean) => JSX.Element);
  onSelectLanguage?: (languageCode: string | null, actions: PopupActions) => void;
  initialLanguageCode?: string | null;
  options?: ILanguageOption[];
}

const ApplicationLanguagePopupForm = ({
  trigger,
  onSelectLanguage,
  initialLanguageCode = null,
  options = [],
}: IApplicationLanguagePopupFormProps) => {
  const popupRef = useRef<PopupActions | null>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const [languageSearch, setLanguageSearch] = useState('');
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string | null>(null);

  const normalizedInitialLanguageCode = useMemo(() => {
    return initialLanguageCode?.split('-')[0] ?? null;
  }, [initialLanguageCode]);

  const mappedLanguages = useMemo(() => {
    return options.map((option) => ({
      id: option.value,
      code: option.value,
      name: option.label,
    }));
  }, [options]);

  const filteredLanguages = useMemo(() => {
    const search = languageSearch.toLowerCase().trim();

    if (!search.length) {
      return mappedLanguages;
    }

    return mappedLanguages.filter((language) => {
      const name = language.name?.toLowerCase() ?? '';
      const code = language.code?.toLowerCase() ?? '';

      return name.includes(search) || code.includes(search);
    });
  }, [mappedLanguages, languageSearch]);

  return (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => {
        setOpen(true);
        setSelectedLanguageCode(normalizedInitialLanguageCode);
      }}
      onClose={() => {
        setOpen(false);
        setSelectedLanguageCode(null);
        setLanguageSearch('');
      }}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        <MagellanPopup.Header>{t('input.label.language')}</MagellanPopup.Header>

        <Input.Text
          value={languageSearch}
          onChange={(e) => setLanguageSearch(e.target.value)}
          name=""
          appBackground
          placeholder={t('input.placeholder.search-language')}
        />

        <div className="flex flex-wrap w-full overflow-auto h-[450px] [scrollbar-width:none] gap-8 max-w-[350px]">
          <div className="flex flex-col w-full gap-2">
            <div className="flex px-8 py-4 text-base opacity-65">{t('popup.all-languages')}</div>

            {filteredLanguages.map((language) => (
              <LanguageSelectCard
                key={language.id}
                language={language as any}
                isSelected={language.code === selectedLanguageCode}
                onClick={(newLanguage: any) =>
                  setSelectedLanguageCode((prevLanguageCode) =>
                    prevLanguageCode === newLanguage.code ? null : newLanguage.code,
                  )
                }
              />
            ))}
          </div>
        </div>

        <div className="flex justify-between w-full">
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>

          <Button
            variant={BUTTON_VARIANT.submit}
            onClick={() =>
              popupRef.current &&
              onSelectLanguage &&
              onSelectLanguage(selectedLanguageCode, popupRef.current)
            }
          >
            <Button.Text text={t('button.save')} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default ApplicationLanguagePopupForm;
